import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { PrjSchedule } from 'models/setup/PrjSchedule';
import {
  CalBoard,
  IncompleteAlertBadge,
  DelayedTagBadge,
  CalEventTitleText,
  TimelineGridContainer,
  TimelineHeaderCol,
  TimelineDayHeaderCell,
  TimelineAllDayLabel,
  TimelineAllDayContent,
  TimelineAllDayChip,
  TimelineTimeAxisCol,
  TimelineTimeAxisGrid,
  TimelineHourLabelCell,
  TimelineDayColumnArea,
  TimelineDragIndicatorBox,
  TimelineDragIndicatorBadge,
  TimelineTimedCard,
  TimelineTimedCardHeader,
  TimelineTimedCardTime,
} from 'components/calendar/Calendar.styles';
import {
  START_HOUR,
  HOUR_HEIGHT,
  TIMELINE_HEIGHT,
  HOURS,
  isAllDayEvent,
  getClickHourTimeByY,
  buildTimedLayouts,
  getScheduleStatus,
  getScheduleStyle,
} from 'utils/CalendarUtils';
import { useTimelineDnd } from './hooks/useTimelineDnd';
import { useTranslation } from 'react-i18next';

interface DayViewProps {
  currentDate: Dayjs;
  schedules: PrjSchedule[];
  selectedCategories: Set<string>;
  isExpanded?: boolean;
  onSelectSchedule: (schedule: PrjSchedule) => void;
  onAddScheduleOnDate: (dateStr: string, timeStr?: string) => void;
  onMoveScheduleDate: (
    schdId: string,
    targetDateStr: string,
    targetTime?: string,
    isAllDay?: boolean
  ) => void;
}

export const DayView: React.FC<DayViewProps> = ({
  currentDate,
  schedules,
  selectedCategories,
  isExpanded = false,
  onSelectSchedule,
  onAddScheduleOnDate,
  onMoveScheduleDate,
}) => {
  const dayStr = currentDate.format('YYYY-MM-DD');
  console.log('dayStr', dayStr);
  console.log('currentDate', currentDate);
  const { t } = useTranslation();
  const {
    handleDragStart,
    handleDragEnd,
    handleTimelineDragOver,
    handleTimelineDragLeave,
    handleTimelineDrop,
    handleAllDayDragOver,
    handleAllDayDragLeave,
    handleAllDayDrop,
    getTimelineIndicator,
    isAllDayDragOver,
  } = useTimelineDnd({ schedules, onMoveScheduleDate });

  const dragIndicator = getTimelineIndicator(dayStr);
  const isDragOverAllDay = isAllDayDragOver(dayStr);

  const { allDayEvents, timedLayouts } = useMemo(() => {
    const daySchedules = schedules.filter((sch) => {
      const startStr = dayjs(sch.planStDtm).format('YYYY-MM-DD');
      const endStr = dayjs(sch.planEndDtm).format('YYYY-MM-DD');
      return dayStr >= startStr && dayStr <= endStr;
    });

    const allDay = daySchedules.filter((sch) => isAllDayEvent(sch));
    const { items: timed } = buildTimedLayouts(schedules, currentDate, { isExpanded });
    return { allDayEvents: allDay, timedLayouts: timed };
  }, [schedules, currentDate, dayStr, isExpanded]);

  return (
    <CalBoard>
      <TimelineGridContainer minWidth={520} gridTemplateColumns="110px minmax(360px, 1fr)">
        <TimelineHeaderCol>{t('com.label.시간', '시간')}</TimelineHeaderCol>
        <TimelineDayHeaderCell>{currentDate.format('YYYY-MM-DD (ddd)')}</TimelineDayHeaderCell>

        {/* All day 영역 (다일 기간 일정) */}
        <TimelineAllDayLabel>All day</TimelineAllDayLabel>
        <TimelineAllDayContent
          isDragOver={isDragOverAllDay}
          onClick={() => onAddScheduleOnDate(dayStr)}
          onDragOver={(e) => handleAllDayDragOver(e, dayStr)}
          onDragLeave={(e) => handleAllDayDragLeave(e, dayStr)}
          onDrop={(e) => handleAllDayDrop(e, dayStr)}
        >
          {allDayEvents.map((sch) => {
            const category = sch.wktTpCd || 'ETC';
            const isDimmed = !selectedCategories.has(category);
            const status = getScheduleStatus(sch, dayStr);
            const style = getScheduleStyle(status, sch.wktTpCd, isDimmed);
            const displayTitle = sch.schdDisplayTitle || sch.schdNm || '일정';
            const showProcAlert = sch.schdProcYn === 'Y';

            return (
              <TimelineAllDayChip
                key={sch.schdId}
                title={`${displayTitle} (${dayjs(sch.planStDtm).format(
                  'YYYY-MM-DD HH:mm'
                )} ~ ${dayjs(sch.planEndDtm).format('YYYY-MM-DD HH:mm')})`}
                draggable={sch.schdStatCd !== 'CPLT'}
                onDragStart={(e) => handleDragStart(e, sch, dayStr, true)}
                onDragEnd={handleDragEnd}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectSchedule(sch);
                }}
                bg={style.bg}
                borderWidth={style.borderWidth}
                borderColor={style.border}
                borderLeft={style.borderLeft}
                textColor={style.text}
                opacity={style.opacity}
                lineThrough={style.lineThrough}
              >
                {showProcAlert && <IncompleteAlertBadge title="진행 중">!</IncompleteAlertBadge>}
                <CalEventTitleText>{displayTitle}</CalEventTitleText>
                {status === 'delayed' && (
                  <DelayedTagBadge>{t('com.label.지연', '지연')}</DelayedTagBadge>
                )}
              </TimelineAllDayChip>
            );
          })}
        </TimelineAllDayContent>

        {/* 시간 축 */}
        <TimelineTimeAxisCol>
          <TimelineTimeAxisGrid height={TIMELINE_HEIGHT} rowCount={HOURS.length}>
            {HOURS.map((hour) => (
              <TimelineHourLabelCell key={`time-${hour}`} isFirst={hour === START_HOUR}>
                {`${String(hour).padStart(2, '0')}:00`}
              </TimelineHourLabelCell>
            ))}
          </TimelineTimeAxisGrid>
        </TimelineTimeAxisCol>

        {/* 타임라인 당일 시간 일정 영역 */}
        <TimelineDayColumnArea
          height={TIMELINE_HEIGHT}
          hourHeight={HOUR_HEIGHT}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const timeStr = getClickHourTimeByY(e.clientY, rect);
            onAddScheduleOnDate(dayStr, timeStr);
          }}
          onDragOver={(e) => handleTimelineDragOver(e, dayStr)}
          onDragLeave={(e) => handleTimelineDragLeave(e, dayStr)}
          onDrop={(e) => handleTimelineDrop(e, dayStr)}
        >
          {/* 드래그 시 변경될 위치에 표시되는 점선 안내 영역 (Placeholder Drop Indicator) */}
          {dragIndicator && (
            <TimelineDragIndicatorBox topPx={dragIndicator.topPx} heightPx={dragIndicator.heightPx}>
              <TimelineDragIndicatorBadge>
                🕒 {dragIndicator.timeLabel} {t('com.label.변경', '변경')}
              </TimelineDragIndicatorBadge>
            </TimelineDragIndicatorBox>
          )}

          {timedLayouts.map((item) => {
            const category = item.sch.wktTpCd || 'ETC';
            const isDimmed = !selectedCategories.has(category);
            const status = getScheduleStatus(item.sch, dayStr);
            const style = getScheduleStyle(status, item.sch.wktTpCd, isDimmed);
            const displayTitle = item.sch.schdDisplayTitle || item.sch.schdNm || '일정';
            const showProcAlert = item.sch.schdProcYn === 'Y';
            const showTimeDetail = item.heightPx >= 40;

            return (
              <TimelineTimedCard
                key={`${item.sch.schdId}-${item.startMin}-${item.col}`}
                title={`${displayTitle} (${dayjs(item.sch.planStDtm).format('HH:mm')} ~ ${dayjs(
                  item.sch.planEndDtm
                ).format('HH:mm')})`}
                draggable={item.sch.schdStatCd !== 'CPLT'}
                onDragStart={(e) => handleDragStart(e, item.sch, dayStr, false)}
                onDragEnd={handleDragEnd}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectSchedule(item.sch);
                }}
                topPx={item.topPx}
                heightPx={item.heightPx}
                leftPct={item.leftPct}
                widthPct={item.widthPct}
                bg={style.bg}
                borderWidth={style.borderWidth}
                borderColor={style.border}
                borderLeft={style.borderLeft}
                textColor={style.text}
                opacity={style.opacity}
                fontSize={item.totalCols > 3 ? 9.5 : item.totalCols > 2 ? 10 : 11}
              >
                <TimelineTimedCardHeader lineThrough={style.lineThrough}>
                  {showProcAlert && <IncompleteAlertBadge title="진행 중">!</IncompleteAlertBadge>}
                  <CalEventTitleText>{displayTitle}</CalEventTitleText>
                  {status === 'delayed' && (
                    <DelayedTagBadge>{t('com.label.지연', '지연')}</DelayedTagBadge>
                  )}
                </TimelineTimedCardHeader>
                {showTimeDetail && (
                  <TimelineTimedCardTime>
                    {dayjs(item.sch.planStDtm).format('HH:mm')} ~{' '}
                    {dayjs(item.sch.planEndDtm).format('HH:mm')}
                  </TimelineTimedCardTime>
                )}
              </TimelineTimedCard>
            );
          })}
        </TimelineDayColumnArea>
      </TimelineGridContainer>
    </CalBoard>
  );
};
